import { ReactNode, useEffect, useMemo, useState } from 'react';
import { cn } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';

const LOADING_VARIANTS = [
  { symbol: '🏠', label: 'Checking the blueprints…' },
  { symbol: '🧰', label: 'Grabbing the right tools…' },
  { symbol: '🎨', label: 'Adding a fresh coat…' },
  { symbol: '📏', label: 'Measuring twice…' },
  { symbol: '💡', label: 'Brewing a bright idea…' },
  { symbol: '🪜', label: 'Setting up the ladder…' },
  { symbol: '📋', label: 'Going through the checklist…' },
  { symbol: '🔎', label: 'Inspecting the floor plan…' },
];

interface DIYLoadingPlaceholderProps {
  /** Override the random message */
  message?: string;
  /** Size of the illustration */
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  /**
   * Skeleton node to swap in after `skeletonDelayMs` (default 500ms).
   * Google mobile UX guidance: transient loads (<500ms) should show a branded
   * indicator; longer loads should show a skeleton mirroring the real layout
   * so perceived performance stays high. (#817)
   */
  skeleton?: ReactNode;
  /** Delay before swapping from emoji to skeleton. Defaults to 500ms. */
  skeletonDelayMs?: number;
}

/**
 * Branded DIY loading placeholder with random illustration.
 * For loads longer than 500ms, pass a `skeleton` prop and the component swaps
 * to that skeleton automatically so the user stops staring at a pulsing emoji.
 */
export function DIYLoadingPlaceholder({ message, size = 'md', className, skeleton, skeletonDelayMs = 500 }: DIYLoadingPlaceholderProps) {
  const variant = useMemo(
    () => LOADING_VARIANTS[Math.floor(Math.random() * LOADING_VARIANTS.length)],
    []
  );

  const [showSkeleton, setShowSkeleton] = useState(false);
  useEffect(() => {
    if (!skeleton) return;
    const id = window.setTimeout(() => setShowSkeleton(true), skeletonDelayMs);
    return () => window.clearTimeout(id);
  }, [skeleton, skeletonDelayMs]);

  if (skeleton && showSkeleton) {
    return <>{skeleton}</>;
  }

  const imgSize = size === 'sm' ? 'h-16 w-16' : size === 'md' ? 'h-24 w-24' : 'h-32 w-32';
  const textSize = size === 'sm' ? 'text-sm' : size === 'md' ? 'text-base' : 'text-lg';
  const padding = size === 'sm' ? 'py-4' : size === 'md' ? 'py-6' : 'py-8';

  return (
    <div className={cn('flex flex-col items-center justify-center gap-3 w-full', padding, className)}>
      <div
        className={cn(
          imgSize,
          'flex items-center justify-center rounded-full bg-muted text-3xl animate-[pulse_2s_ease-in-out_infinite]'
        )}
        aria-hidden="true"
      >
        <span>{variant.symbol}</span>
      </div>
      <span className={cn(textSize, 'text-muted-foreground font-medium animate-[pulse_2s_ease-in-out_infinite]')}>
        {message || variant.label}
      </span>
    </div>
  );
}

/** Skeleton mirroring the DIY project grid on the dashboard. Used for loads >500ms. (#817) */
export function DIYProjectCardsSkeleton({ count = 6 }: { count?: number }) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label="Loading projects"
      className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 w-full"
    >
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-2xl border border-border/50 bg-card overflow-hidden">
          <Skeleton className="h-32 w-full rounded-none" />
          <div className="p-4 space-y-2">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-3 w-1/2" />
            <div className="flex gap-2 pt-2">
              <Skeleton className="h-5 w-16 rounded-full" />
              <Skeleton className="h-5 w-12 rounded-full" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

/** Skeleton mirroring the DIY step list in project detail. Used for loads >500ms. (#817) */
export function DIYStepListSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div
      role="status"
      aria-busy="true"
      aria-label="Loading steps"
      className="space-y-3 w-full"
    >
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="rounded-xl border border-border/50 bg-card p-4 space-y-2">
          <div className="flex items-start gap-3">
            <Skeleton className="h-5 w-5 rounded-md shrink-0" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-4/5" />
              <Skeleton className="h-3 w-3/5" />
            </div>
          </div>
          <div className="flex gap-2 pt-1 pl-8">
            <Skeleton className="h-5 w-14 rounded-full" />
            <Skeleton className="h-5 w-20 rounded-full" />
          </div>
        </div>
      ))}
    </div>
  );
}
